import React from "react";
import {
  Box,
  Flex,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ISensorAlertConfigLightning } from "stores/data/insightAlerts/insightAlertModel";
import { SA_COLORS } from "../sensor-alert-colors";
import { TogglePillButton } from "../TogglePillButton";
import {
  LIGHTNING_RECURRENCE_HOURS_OPTIONS,
  LIGHTNING_SILENCE_WINDOW_MIN_MAX,
  LIGHTNING_SILENCE_WINDOW_MIN_MIN,
  LIGHTNING_SILENCE_WINDOW_MIN_STEP,
  LightningPeriodicity,
  notificationWindowMinToPeriodicity,
  periodicityToNotificationWindowMin,
} from "./lightning-constants";

const FIELD_MAX_WIDTH = "280px";

/**
 * Lightning post-selection form (rendered inside the drawer once a point + radius are placed).
 *
 * Visual identity follows Figma node 3575:3545 (sensor alert configuration card).
 * Reuses Chakra primitives + shared SensorAlertSection tokens — no new design system.
 *
 * Spec 043 §FR-003 + §FR-005 + §Decisions(2 — visual identity preserved).
 */
export interface LightningTabContentProps {
  /** Current lightning slice — never null when this component is rendered. */
  slice: ISensorAlertConfigLightning;
  /** Update callback receiving the partial change. */
  onChange: (next: ISensorAlertConfigLightning) => void;
}

interface FieldHeadingProps {
  title: string;
  description: string;
}

const FieldHeading: React.FC<FieldHeadingProps> = ({ title, description }) => (
  <Box flex="1">
    <Text
      fontWeight="600"
      fontSize="14px"
      lineHeight="1.21"
      color={SA_COLORS.textTitle}
      mb="16px"
    >
      {title}
    </Text>
    <Text
      fontWeight="400"
      fontSize="14px"
      lineHeight="1.21"
      color={SA_COLORS.textBody}
    >
      {description}
    </Text>
  </Box>
);

export const LightningTabContent: React.FC<LightningTabContentProps> = ({
  slice,
  onChange,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.sensorAlert",
  });

  const { periodicity, recurrenceHours } = notificationWindowMinToPeriodicity(
    slice.notificationWindowMin,
  );

  const setAllClearMin = (min: number) =>
    onChange({ ...slice, allClearMin: min });

  const setPeriodicity = (next: LightningPeriodicity) => {
    onChange({
      ...slice,
      notificationWindowMin: periodicityToNotificationWindowMin(
        next,
        recurrenceHours,
      ),
    });
  };

  const setRecurrenceHours = (h: number) =>
    onChange({
      ...slice,
      notificationWindowMin: periodicityToNotificationWindowMin("recurring", h),
    });

  return (
    <Box
      border="1px solid"
      borderColor={SA_COLORS.panelBorder}
      borderRadius="8px"
      padding="16px"
      display="flex"
      flexDirection="column"
      gap="24px"
    >
      {/* Janela de silêncio — title+hint left, select right */}
      <Flex justify="space-between" align="center" gap="16px">
        <FieldHeading
          title={t("lightningSilenceWindowLabel")}
          description={t("lightningSilenceWindowHint")}
        />
        <NumberInput
          value={slice.allClearMin}
          onChange={(_, num) => {
            // Chakra fires onChange while editing with NaN for empty/partial
            // input — only commit valid numbers within bounds.
            if (Number.isNaN(num)) return;
            setAllClearMin(num);
          }}
          min={LIGHTNING_SILENCE_WINDOW_MIN_MIN}
          max={LIGHTNING_SILENCE_WINDOW_MIN_MAX}
          step={LIGHTNING_SILENCE_WINDOW_MIN_STEP}
          clampValueOnBlur
          maxW={FIELD_MAX_WIDTH}
        >
          <NumberInputField
            aria-label={t("lightningSilenceWindowLabel") ?? undefined}
          />
          <NumberInputStepper>
            <NumberIncrementStepper aria-label={t("increase") ?? "Aumentar"} />
            <NumberDecrementStepper aria-label={t("decrease") ?? "Diminuir"} />
          </NumberInputStepper>
        </NumberInput>
      </Flex>

      {/* Periodicidade do alerta — title + 2 toggle buttons */}
      <VStack align="stretch" spacing="16px">
        <Text
          fontWeight="500"
          fontSize="14px"
          lineHeight="1.71"
          color={SA_COLORS.textTitle}
        >
          {t("periodicityLabel")}
        </Text>
        <Flex gap="16px">
          <TogglePillButton
            active={periodicity === "recurring"}
            onClick={() => setPeriodicity("recurring")}
            label={t("periodicityRecurring")}
          />
          <TogglePillButton
            active={periodicity === "single"}
            onClick={() => setPeriodicity("single")}
            label={t("periodicitySingle")}
          />
        </Flex>
      </VStack>

      {/* Recorrente description + interval select (only when recurring) */}
      {periodicity === "recurring" && (
        <Flex justify="space-between" align="center" gap="10px">
          <FieldHeading
            title={t("periodicityRecurring")}
            description={t("recurringDescription")}
          />
          <Box flex="1" maxW={FIELD_MAX_WIDTH}>
            <Text
              fontWeight="400"
              fontSize="14px"
              lineHeight="1.43"
              color={SA_COLORS.textFormLabel}
              mb="6px"
            >
              {t("frequencyLabel")}
            </Text>
            <Select
              value={recurrenceHours}
              onChange={(e) => setRecurrenceHours(Number(e.target.value))}
              aria-label={t("frequencyLabel") ?? undefined}
            >
              {LIGHTNING_RECURRENCE_HOURS_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {t(h === 1 ? "frequencyHour_one" : "frequencyHour", {
                    count: h,
                  })}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>
      )}

      {/* Único description — visible whenever periodicity is single (Figma keeps
          it pinned at the bottom but showing only when relevant avoids the UX
          oddity of describing an inactive option). */}
      {periodicity === "single" && (
        <FieldHeading
          title={t("periodicitySingle")}
          description={t("singleDescription")}
        />
      )}
    </Box>
  );
};
